<template>
    <modal
        id="employee-list-modal"
        size="large"
        v-model="showModal"
        :title="from === 'user' ? generateModalTitle('user') : generateModalTitle('Employee')"
        @submit="submit"
        :loading="loading"
        :btn-label="selectedUrl ? $t('save') : $t('add')"
        :preloader="preloader">

        <form
            v-if="isMailSettingExist || selectedUrl"
            ref="form"
            :data-url='selectedUrl ? selectedUrl : EMPLOYEE_CREATE'
            @submit.prevent="submitData"
        >
            <div class="row">
                <div class="col-md-6">
                    <app-form-group
                        :label="$t('first_name')"
                        :placeholder="$placeholder('first_name')"
                        v-model="formData.first_name"
                        :required="true"
                        :error-message="$errorMessage(errors, 'first_name')"
                    />
                </div>
                <div class="col-md-6">
                    <app-form-group
                        :label="$t('last_name')"
                        :placeholder="$placeholder('last_name')"
                        v-model="formData.last_name"
                        :required="true"
                        :error-message="$errorMessage(errors, 'last_name')"
                    />
                </div>
            </div>

            <app-form-group
                :label="$t('email')"
                :placeholder="$placeholder('email')"
                v-model="formData.email"
                :required="true"
                :error-message="$errorMessage(errors, 'email')"
                type="email"
            />
            <app-form-group
                :label="$t('password')"
                type="password"
                id="input-text-password"
                :placeholder="$t('password')"
                v-model="formData.password"
                :error-message="$errorMessage(errors, 'password')"
                :show-password="true"
            />
            <app-form-group
                :label="$fieldTitle('confirm', 'password')"
                type="password"
                id="input-text-confirm-password"
                :placeholder="$placeholder('confirm', 'password')"
                v-model="formData.password_confirmation"
                :show-password="true"
            />
            <app-form-group
                :label="$t('gender')"
                type="radio"
                :list="[
                    {id:'male',value: $t('male')},
                    {id:'female', value:  $t('female')},
                    {id:'other', value:  $t('other')}
                ]"
                v-model="formData.gender"
                :error-message="$errorMessage(errors, 'gender')"
            />
            <div class="row">
                <div class="col-md-6">
                    <app-form-group
                        :label="$t('employee_id') + ` (` + $t('latest_id') + `: ${latest_employee_id})`"
                        :placeholder="$placeholder('employee_id')"
                        v-model="formData.employee_id"
                        :required="true"
                        :error-message="$errorMessage(errors, 'employee_id',true,true)"
                    />
                </div>
                <div class="col-md-6">
                    <app-form-group-selectable
                        type="select"
                        :label="$t('department')"
                        list-value-field="name"
                        v-model="formData.department_id"
                        :chooseLabel="$t('department')"
                        :error-message="$errorMessage(errors, 'department_id')"
                        :fetch-url="SELECTABLE_DEPARTMENT"
                    />
                </div>
            </div>
            <div class="row">
                <div class="col-md-6">
                    <app-form-group-selectable
                        type="select"
                        :label="$t('designation')"
                        list-value-field="name"
                        v-model="formData.designation_id"
                        :chooseLabel="$t('designation')"
                        :error-message="$errorMessage(errors, 'designation_id')"
                        :fetch-url="SELECTABLE_DESIGNATION"
                    />
                </div>
                <div class="col-md-6">
                    <app-form-group-selectable
                        type="select"
                        :label="$t('employment_status')"
                        list-value-field="name"
                        v-model="formData.employment_status_id"
                        :chooseLabel="$t('employment_status')"
                        :error-message="$errorMessage(errors, 'employment_status_id')"
                        :fetch-url="`${SELECTABLE_EMPLOYMENT_STATUS}?excluded=terminated`"
                    />
                </div>
            </div>
            <div class="row" v-if="!selectedUrl">
                <div class="col-md-6">
                    <app-form-group
                        type="number"
                        :label="$t('salary')"
                        :placeholder="$placeholder('salary')"
                        v-model="formData.salary"
                        :required="true"
                        :error-message="$errorMessage(errors, 'salary')"
                    />
                </div>
                <div class="col-md-6">
                    <app-form-group
                        :label="$t('salary_type')"
                        type="select"
                        :list="[
                            {id:'hourly',value: $t('Hourly')},
                            {id:'daily', value:  $t('Daily')},
                            {id:'monthly', value:  $t('Monthly')}
                        ]"
                        v-model="formData.salary_type"
                        :error-message="$errorMessage(errors, 'salary_type')"
                    />
                </div>
            </div>
            <app-form-group
                type="date"
                :label="$t('joining_date')"
                :placeholder="$placeholder('joining_date')"
                v-model="formData.joining_date"
                :required="true"
                :error-message="$errorMessage(errors, 'joining_date')"
            />
            <app-form-group
                v-if="from === 'user'"
                class="mb-primary"
                type="single-checkbox"
                label=""
                :list-value-field="$t('do_not_show_in_employee_list')"
                v-model="formData.dont_show_in_employee"
            />
            <app-form-group-selectable
                type="multi-select"
                v-if="$can('attach_users_to_roles')"
                :label="$t('role')"
                list-value-field="name"
                v-model="formData.roles"
                :chooseLabel="$t('role')"
                :error-message="$errorMessage(errors, 'roles')"
                :fetch-url="SELECTABLE_ROLE"
            />
            <app-form-group-selectable
                type="multi-select"
                :label="$t('employee_tags')"
                list-value-field="name"
                v-model="formData.employee_tags"
                :chooseLabel="$t('employee_tags')"
                :fetch-url="SELECTABLE_EMPLOYEE_TAGS"
            />
        </form>
        <app-note v-else
                  :title="$t('no_delivery_settings_found')"
                  :notes="$optional(currentTenant, 'is_single') ?
                  `<ol>
                     <li>${$t('cron_job_settings_warning',{
                      documentation: `<a href='#' target='_blank'>${$t('documentation')}</a>`
                  })}</li>
                     <li>${$t('no_delivery_settings_warning', {
                      location: `<a href='${urlGenerator(TENANT_EMAIL_SETUP_SETTING)}'>${$t('here')}</a>`
                  })}</li>
                  </ol>`: [$t('no_delivery_settings_warning_none')]"
                  content-type="html"
        />
        <template v-if="!isMailSettingExist && !selectedUrl" slot="footer">
            <button type="button"
                    class="btn btn-secondary"
                    data-dismiss="modal">
                {{ $t('close') }}
            </button>
        </template>
    </modal>
</template>

<script>
import FormHelperMixins from "../../../../common/Mixin/Global/FormHelperMixins";
import ModalMixin from "../../../../common/Mixin/Global/ModalMixin";
import {
    EMPLOYEES_LIST,
    SELECTABLE_DEPARTMENT,
    SELECTABLE_ROLE,
    SELECTABLE_DESIGNATION,
    SELECTABLE_EMPLOYMENT_STATUS,
    SELECTABLE_EMPLOYEE_TAGS,
    EMPLOYEE_CREATE, AUTO_EMPLOYEE_ID, LATEST_EMPLOYEE_ID,
} from "../../../Config/ApiUrl";
import {
    TENANT_MAIL_CHECK_URL,
    TENANT_EMAIL_SETUP_SETTING
} from "../../../../common/Config/apiUrl";
import {axiosGet} from "../../../../common/Helper/AxiosHelper";
import {formatDateForServer} from "../../../../common/Helper/Support/DateTimeHelper";

export default {
    name  : "EmployeeCreateEditModel",
    mixins: [FormHelperMixins, ModalMixin],
    props : {
        candidate: null,
        from     : {
            type   : String,
            default: 'employee'
        }
    },
    data() {
        const formData = {
            employee_id      : '',
            department       : {},
            roles            : [],
            employee_tags    : [],
            designation      : {},
            employment_status: {},
            is_in_employee   : false,
            salary_type      : 'monthly',
        }

        if (this.candidate) {
            formData.first_name   = this.candidate.name_romaji
            formData.last_name    = this.candidate.name_katakana
            formData.email        = this.candidate.email
            formData.gender       = this.candidate.gender
            formData.phone_number = this.candidate.phone
            formData.about_me     = this.candidate.remark
        }

        return {
            isMailSettingExist: true,
            latest_employee_id: '',
            formData          : formData,
            EMPLOYEES_LIST,
            SELECTABLE_DEPARTMENT,
            SELECTABLE_ROLE,
            SELECTABLE_DESIGNATION,
            SELECTABLE_EMPLOYMENT_STATUS,
            EMPLOYEE_CREATE,
            TENANT_EMAIL_SETUP_SETTING,
            SELECTABLE_EMPLOYEE_TAGS,
        }
    },
    methods: {
        submit() {
            if (!this.$refs.form.checkValidity()) {
                this.$refs.form.reportValidity();
                return;
            }

            this.fieldStatus.isSubmit    = true;
            this.loading                 = true;
            this.message                 = '';
            this.errors                  = {};
            this.formData.is_in_employee = this.formData.dont_show_in_employee ? 0 : 1;
            this.formData.joining_date   = formatDateForServer(this.formData.joining_date);
            this.submitData(this.formData);
        },
        afterSuccess({data}) {
            this.formData = {};
            $('#employee-list-modal').modal('hide');
            this.$emit('input', false);
            this.toastAndReload(data.message, this.from === 'employee' ? 'employee-table' : 'user-table');
            if (this.from === 'user') {
                this.$hub.$emit(`reload-role-table`)
            }
        },
        afterSuccessFromGetEditData({data}) {
            this.preloader                      = false;
            //Before customization this was "  this.formData = data;"
            this.formData                       = {...this.formData, ...data};
            this.formData.employee_id           = data.profile?.employee_id;
            this.formData.roles                 = this.collection(data.roles).pluck();
            this.formData.designation_id        = data.designation?.id;
            this.formData.department_id         = data.department?.id;
            this.formData.employment_status_id  = data.employment_status?.id;
            this.formData.dont_show_in_employee = parseInt(this.formData.is_in_employee) ? 0 : 1;
            this.formData.joining_date          = data.profile?.joining_date ? new Date(data.profile?.joining_date) : null;
            this.formData.gender                = data.profile?.gender;
            this.formData.employee_tags         = this.collection(data.employee_tags).pluck();
        },
        checkMailSettings() {
            this.preloader = true;
            axiosGet(TENANT_MAIL_CHECK_URL).then(response => {
                this.isMailSettingExist = !!response.data;
            }).finally(() => {
                this.preloader = false;
            });
        },
        generateModalTitle(subject) {
            return this.$fieldTitle(this.selectedUrl ? 'edit' : 'add', subject, true);
        },
        checkEmployeeID() {
            axiosGet(AUTO_EMPLOYEE_ID).then(response => {
                // this.formData.employee_id = response.data;
            }).finally(() => {
                this.preloader = false;
            });
        }
    },
    created() {
        this.checkMailSettings();
        axiosGet(LATEST_EMPLOYEE_ID).then(response => {
            this.latest_employee_id = response.data;
        });
        if (!this.selectedUrl) {
            this.checkEmployeeID();
        }
    }
}
</script>